import React from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ReportsEditor from 'modules/reports/components/Editor'
import NotFound from 'modules/reports/components/NotFound'
import SaveModal, { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import ReportsSqlEditor from 'modules/reports/components/SqlEditor'
import useReport from 'modules/reports/hooks/useReport'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import useStore from 'modules/reports/hooks/useStore'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import { ReportEditorStore } from 'modules/reports/lib/Store'
import { PromptProvider } from 'modules/reports/lib/promptContext'
import type Report from 'modules/reports/store/models/Report'

const onReportFound = (model: Report) => {
  model.set({
    title: i18n.t('custom_reports.editor.copy_of', {
      title: model.attributes.title,
    }),
  })
}

interface ContentProps {
  report: Report | null | undefined
  store: ReportEditorStore
}

const Content = ({ report, store }: ContentProps) => {
  if (report == null) return null

  const isSqlReport = report.sqlQuery != null

  if (isSqlReport) {
    return <ReportsSqlEditor store={store} />
  }

  return <ReportsEditor store={store} />
}

const CloneReportPage = () => {
  const params = useParams()
  const [search, _] = useSearchParams()
  const { loading } = useOutletContext<RouteContextParams>()

  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )

  const { report, isNotFound } = useReport({
    id: Number(params.id),
    loading,
    onReportFound,
  })

  const store = useStore({
    report,
    origin: report?.origin,
  })

  const { track } = useReportTracking(store)

  useUnsafeEffect(() => {
    if (!report) return

    track(trackingEvents.reports.newReportFromClone, {})
  }, [track, report])

  const isAuthorized = featureAvailability.available

  const showSaveModal = search.get('action') == SAVE_ACTION

  return (
    <ResponsivePage.Body
      loading={isFeatureLoading || loading}
      authorized={isAuthorized}
      showHeader={false}
      error={undefined}
      variant="full"
    >
      <NotFound notFound={isNotFound}>
        <PromptProvider>
          <Box flexDirection="column">
            {showSaveModal && report && store && <SaveModal report={report} store={store} clone />}
            {report && store && <Content report={report} store={store} />}
          </Box>
        </PromptProvider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default CloneReportPage
